/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useEffect, useRef } from "react";
import lottie from "lottie-web";
import { cn } from "~/lib/utils";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

const parentChildPhone = imageUrl("/images/connecting-family/parent-child-phone.webp");

/**
 * Ported from bouncieweb's `LandingPageComponents/FeaturedQuote.js`.
 *
 * Faithful reproduction of the featured-quote section: an image (with the
 * "amoeba" Lottie blob behind it) beside a large display-font quote framed by
 * two SVG quote marks, plus an optional addendum block.
 *
 * The original received a Next static image import for `image`; here it is an
 * optional URL string that defaults to the copied `parent-child-phone.webp`.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  md 768px -> md:  lg 1024px -> lg:  xlg 1280px -> xl:
 */

/**
 * Ported from `LottieAnimation` — the "amoeba" blob loaded by public path inside
 * a useEffect so SSR never touches lottie. Matches the FamilyHero approach.
 */
function AmoebaLottie({ className }: { className?: string }) {
  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!animationRef.current) return;
    const anim = lottie.loadAnimation({
      container: animationRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/landing/amoeba.json",
    });
    return () => anim.destroy();
  }, []);

  return <div ref={animationRef} className={className} />;
}

/**
 * The SVG quote-mark glyph, reproduced from the original `QuoteSymbolSVG`.
 * `width` is expressed in `em` so it scales with the surrounding font size.
 */
function QuoteSymbolSVG({ width = 1.6 }: { width?: number }) {
  return (
    <svg
      width={`${width}em`}
      height={`${width / (68 / 56)}em`}
      viewBox="0 0 68 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 55.62V23.4C0 7.92 8.64 0 25.74 0H29.16V11.52H25.74C17.82 11.52 13.86 15.48 13.86 23.4V28.62H29.34V55.62H0ZM38.16 55.62V23.4C38.16 7.92 46.8 0 63.9 0H67.32V11.52H63.9C55.98 11.52 52.02 15.48 52.02 23.4V28.62H67.5V55.62H38.16Z"
        fill="#D2DFF8"
        fillOpacity="0.6"
      />
    </svg>
  );
}

export default function FeaturedQuote({
  quoteText,
  imageAlt,
  closeQuoteLeft,
  addendum,
  image,
}: {
  quoteText: React.ReactNode;
  imageAlt: string;
  closeQuoteLeft?: string;
  addendum?: React.ReactNode;
  image?: string;
}) {
  const imageSrc = image ?? parentChildPhone;

  return (
    // FamilyFeaturedQuoteSection
    <div className="flex flex-col items-center justify-center gap-4 pt-12 md:flex-row lg:gap-10">
      {/* QuoteImageWrapper */}
      <div className="relative w-full p-8 min-[431px]:w-[24rem] md:w-[20rem] lg:w-[26rem] xl:w-[29rem]">
        <AmoebaLottie className="absolute top-1/2 left-1/2 w-[107%] -translate-x-1/2 -translate-y-1/2" />
        {/* QuoteImage */}
        <Picture
          src={imageSrc}
          alt={imageAlt}
          className="relative h-fit w-full"
        />
      </div>
      {/* Text */}
      <div className="ml-[1.5em]">
        {/* Quote */}
        <div
          className={cn(
            "relative m-0 font-display font-extrabold leading-none",
            "text-[8vw] min-[431px]:text-[37px] md:text-[30px] lg:text-[40px] xl:text-[48px]",
          )}
        >
          {/* QuoteSymbolOpen: top 0em, left -0.5em */}
          <div className="font-display absolute top-0 left-[-0.5em] -translate-x-1/2 -translate-y-1/2 leading-[0] font-extrabold text-[#D9E4F8] select-none">
            <QuoteSymbolSVG />
          </div>
          {/* QuoteSymbolClose: bottom -1.2em, left=closeQuoteLeft, rotated 180 */}
          <div
            className="font-display absolute bottom-[-1.2em] [transform:translate(-50%,-50%)_rotate(180deg)] leading-[0] font-extrabold text-[#D9E4F8] select-none"
            style={{ left: closeQuoteLeft }}
          >
            <QuoteSymbolSVG />
          </div>
          {/* QuoteText */}
          <div className="relative text-[1em] [text-wrap:nowrap]">
            {quoteText}
          </div>
        </div>
        {/* AddendumWrapper */}
        {addendum ? (
          <div
            className={cn(
              "mt-8 leading-[0.5] md:mt-10",
              "[&>p]:font-bold [&>p]:text-[#805A96] [&>p]:text-[14px] md:[&>p]:text-[16px]",
              "[&>a]:text-[#1F69F2] [&>a]:text-[12px] md:[&>a]:text-[14px]",
            )}
          >
            {addendum}
          </div>
        ) : null}
      </div>
    </div>
  );
}
